import { api } from '../contexts/AuthContext';

export function isPushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ apresenta-se como Mac com touch
  const iPadOS = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  return iOSDevice || iPadOS;
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.navigator.standalone === true
    || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
}

// Estado do suporte a push para orientar o utilizador na UI
// 'supported' | 'ios-needs-install' | 'unsupported'
export function getPushSupportState() {
  if (isPushSupported()) return 'supported';
  if (isIOS() && !isStandalone()) return 'ios-needs-install';
  return 'unsupported';
}

export function getPushPermission() {
  return isPushSupported() ? Notification.permission : 'unsupported';
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function registerPushServiceWorker() {
  if (!isPushSupported()) return null;
  return navigator.serviceWorker.register('/push-sw.js', { scope: '/' });
}

export async function isPushSubscribed() {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error('unsupported');
  if (Notification.permission === 'denied') throw new Error('denied');
  const reg = await registerPushServiceWorker();
  const { data } = await api.get('/push/vapid-public-key');
  if (!data?.publicKey) throw new Error('no-key');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('not-granted');
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  await api.post('/push/subscribe', { subscription: sub.toJSON() });
  return sub;
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await api.post('/push/unsubscribe', { subscription: sub.toJSON() }); } catch { /* noop */ }
    await sub.unsubscribe();
  }
}
